import { useMemo, useRef } from "react";
import { useForm, useSelector, type StandardSchemaV1Issue } from "@tanstack/react-form";

import { Modal } from "@/primitives/Modal";
import { Form } from "@/primitives/Form";
import { Field } from "@/primitives/Field";
import { FieldArray } from "@/primitives/FieldArray";
import { Input } from "@/primitives/Input";
import { InputGroup } from "@/primitives/InputGroup";
import { Button } from "@/primitives/Button";
import { Select } from "@/primitives/Select";
import { FA6Icon } from "@/primitives/FA6Icon";
import { api } from "@/shared/api";
import type { ErrorResponse, OAuthClient } from "@/shared/timr";
import {
	createOAuthClientCreateSchema,
	createRedirectUriItemSchema,
	GRANT_TYPE_AUTHORIZATION_CODE,
	GRANT_TYPE_CLIENT_CREDENTIALS,
	normalizeRedirectUris,
	type OAuthClientCreateFormValues,
} from "./oAuthClientSchema";

export interface OAuthClientCreateDialogProps {
	onCreated: (oAuthClient: OAuthClient) => void;
}

const defaultValues: OAuthClientCreateFormValues = {
	oAuthClientName: "",
	authorizationGrantTypes: null,
	// one empty row so an input is ready as soon as the authorization-code flow is picked
	redirectUris: [""],
};

export function OAuthClientCreateDialog({ onCreated }: OAuthClientCreateDialogProps) {
	const schema = useMemo(() => createOAuthClientCreateSchema(), []);
	const redirectUriItemSchema = useMemo(() => createRedirectUriItemSchema(), []);

	// null = "please select", selectable like a placeholder option; the schema rejects it.
	const grantTypeItems = useMemo(
		() => [
			{ label: messages["oAuthClient.oAuthGrantType.placeholder"], value: null },
			{ label: messages["oAuthClient.authorizationGrantTypes.clientCredentials"], value: GRANT_TYPE_CLIENT_CREDENTIALS },
			{ label: messages["oAuthClient.authorizationGrantTypes.authorizationCode"], value: GRANT_TYPE_AUTHORIZATION_CODE },
		],
		[],
	);

	// Opening the select popup moves focus off the trigger, so a plain trigger-onBlur would
	// surface the required error while the user is still choosing. Blur therefore only counts
	// when the trigger is left while closed, or when the popup closes.
	const grantTypeSelectOpen = useRef(false);

	const form = useForm({
		defaultValues,
		validators: {
			onMount: schema,
			onChange: schema,
			onSubmitAsync: async ({ value }) => {
				const usesAuthorizationCode = value.authorizationGrantTypes === GRANT_TYPE_AUTHORIZATION_CODE;
				try {
					const created = await api("/admin/createOAuthClient.json").post({
						oAuthClientName: value.oAuthClientName.trim(),
						authorizationGrantTypes: value.authorizationGrantTypes,
						redirectUris: usesAuthorizationCode ? normalizeRedirectUris(value.redirectUris).join("\n") : "",
					});
					onCreated(created);
				} catch (response: unknown) {
					const fieldErrors = (response as ErrorResponse).responseJSON?.fieldErrors;
					if (fieldErrors != null) {
						return {
							fields: Object.fromEntries(fieldErrors.map((e) => [e.field, e.message])),
						};
					}
				}
			},
		},
	});

	const submitted = useSelector(form.store, (state) => state.submissionAttempts > 0);
	const usesAuthorizationCode = useSelector(
		form.store,
		(state) => state.values.authorizationGrantTypes === GRANT_TYPE_AUTHORIZATION_CODE,
	);

	const errorFor = (meta: { isBlurred: boolean; errors: readonly unknown[] }): string | null => {
		if (!meta.isBlurred && !submitted) return null;
		const error = meta.errors[0];
		if (typeof error === "string") return error;
		const message = (error as StandardSchemaV1Issue)?.message;
		return typeof message === "string" ? message : null;
	};

	return (
		<Modal.Content>
			<Modal.Header>
				<Modal.Title>{messages["oAuthClient.header.create"]}</Modal.Title>
			</Modal.Header>

			<Form
				orientation="horizontal"
				noValidate /* block native form validation */
				onSubmitCapture={(event) => {
					event.preventDefault();
					event.stopPropagation();
					void form.handleSubmit();
				}}
			>
				<Modal.Body>
					<form.Field name="oAuthClientName">
						{(field) => {
							const error = errorFor(field.state.meta);
							return (
								<Field.Root name="oAuthClientName" invalid={error != null}>
									<Field.Label>{messages["admin.oAuthClient.name"]}</Field.Label>
									<Input
										autoFocus
										maxLength={255}
										value={field.state.value}
										onChange={(event) => field.handleChange(event.target.value)}
										onBlur={field.handleBlur}
									/>
									<Field.Error match={error != null}>{error}</Field.Error>
								</Field.Root>
							);
						}}
					</form.Field>

					<form.Field name="authorizationGrantTypes">
						{(field) => {
							const error = errorFor(field.state.meta);
							return (
								<Field.Root name="authorizationGrantTypes" invalid={error != null}>
									<Field.Label>{messages["oAuthClient.oAuthGrantType"]}</Field.Label>
									<Select.Root
										items={grantTypeItems}
										value={field.state.value}
										onValueChange={(value) => field.handleChange(value)}
										onOpenChange={(open) => {
											grantTypeSelectOpen.current = open;
											if (!open) field.handleBlur();
										}}
									>
										<Select.Trigger
											onBlur={() => {
												if (!grantTypeSelectOpen.current) field.handleBlur();
											}}
										/>
										<Select.Items />
									</Select.Root>
									<Field.Error match={error != null}>{error}</Field.Error>
								</Field.Root>
							);
						}}
					</form.Field>

					{usesAuthorizationCode && (
						<form.Field name="redirectUris" mode="array">
							{(field) => {
								const error = errorFor(field.state.meta);
								return (
									<FieldArray.Root
										items={field.state.value}
										onItemsChange={(items) => field.handleChange(items)}
										schema={redirectUriItemSchema}
									>
										<FieldArray.Items>
											{({ value, index, setValue, remove, validate, isOnly, onKeyDown }) => (
												// display timing stays with Base UI: the row's message appears on blur, not while typing
												<Field.Root name={`redirectUris-${index}`} validationMode="onBlur" validate={validate}>
													<Field.Label>{index === 0 ? messages["oAuthClient.redirectUris"] : ""}</Field.Label>
													<InputGroup.Root>
														<InputGroup.Addon square>
															<FA6Icon icon="link" variant="regular" />
														</InputGroup.Addon>
														<Input
															aria-label={`${messages["oAuthClient.redirectUris"]} ${index + 1}`}
															placeholder="https://"
															value={value}
															onChange={(event) => setValue(event.target.value)}
															onBlur={field.handleBlur}
															onKeyDown={onKeyDown}
														/>
														<InputGroup.Button
															aria-label={messages["dialog.delete"]}
															// the last remaining row must stay — the flow needs at least one redirect URL
															disabled={isOnly}
															onClick={remove}
														>
															<FA6Icon icon="trash-can" variant="light" />
														</InputGroup.Button>
													</InputGroup.Root>
													{/* childless: renders whatever the row's validate returned */}
													<Field.Error />
												</Field.Root>
											)}
										</FieldArray.Items>
										<Field.Root invalid={error != null}>
											<Field.Label />
											<div>
												<FieldArray.Add variant="secondary" requireValid>
													<FA6Icon icon="plus" variant="light" className="position-left" />
													{messages["oAuthClient.button.add"]}
												</FieldArray.Add>
											</div>
											<Field.Error match={error != null}>{error}</Field.Error>
										</Field.Root>
									</FieldArray.Root>
								);
							}}
						</form.Field>
					)}
				</Modal.Body>

				<Modal.Footer>
					<Modal.Close render={<Button variant="secondary" type="button" />}>
						{messages["dialog.cancel"]}
					</Modal.Close>
					<form.Subscribe selector={(state) => state.isSubmitting}>
						{(isSubmitting) => (
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting && (
									<FA6Icon icon="spinner-third" variant="regular" className="spinner mr-5" />
								)}
								{messages["dialog.save"]}
							</Button>
						)}
					</form.Subscribe>
				</Modal.Footer>
			</Form>
		</Modal.Content>
	);
}
